import { useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { toString } from 'lodash';

import { FileImage } from '@/common/constants';
import useCreateDocumentByQRCodeQuery from '@/modules/transaction/components/ReimburseMe/queries/useCreateDocumentByQRCode.query';
import useUploadFileByQrLinkStore from '@/modules/transaction/components/ReimburseMe/UploadFileQRCode/store/useUploadFileByQrLink.store';
import useUploadFileToS3Query from '@/modules/transaction/queries/useUploadFileToS3.query';
import { getFileType } from '@/utils/modifiers/getFileType';

import useCompleteFileUploadByQrCodeQuery from './useCompleteFileUploadByQrCode.query';
import useCreateFileUploadUrlByQrCodeQuery from './useCreateFileUploadUrlByQrCode.query';

const isExpired = (validTo?: string | null) => {
  if (!validTo) {
    return true;
  }

  const currentDate = dayjs().toDate();
  const validToDate = dayjs(validTo).toDate();

  return currentDate > validToDate;
};

export const getBlob = async (url: string) => fetch(url)
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  .then(async (response: any) => {
    const blob = await response?.arrayBuffer();

    return new Blob(
      [blob],
      { type: getFileType(url) },
    );
  });

const useFileUploadByQrQuery = () => {
  const { docId, employeeId, qrId, validTo } = useUploadFileByQrLinkStore((state) => state.state);
  const { createDocument, cancelCreateDocument } = useCreateDocumentByQRCodeQuery();
  const { createFileUploadUrlByQrCode, cancelCreateFileUploadUrl } = useCreateFileUploadUrlByQrCodeQuery();
  const { uploadFileToS3, cancelUploadToS3 } = useUploadFileToS3Query();
  const { completeFileUploadByQrCode, cancelCompleteFileUpload } = useCompleteFileUploadByQrCodeQuery();

  const controllerRef = useRef(new AbortController());

  const createDocumentByQr = useCallback(async () => {
    if (!qrId || !employeeId) return undefined;

    if (docId) return docId;
    const { data: { document_id: newDocId } } = await createDocument({
      qrId,
      employeeId: Number(employeeId),
    });
    return newDocId;
  }, [createDocument, docId, employeeId, qrId]);

  const uploadFile = useCallback(async (file: FileImage) => {
    if (!qrId || !employeeId) return;

    if (isExpired(validTo)) {
      return;
    }

    controllerRef.current = new AbortController();

    const documentId = await createDocumentByQr();

    if (!documentId) return;

    try {
      const uploadUrl = await createFileUploadUrlByQrCode({
        documentId: toString(documentId),
        employeeId,
        name: file?.name,
        qrId,
      });

      if (!uploadUrl?.data?.upload_url) {
        return;
      }

      const previewResponse = await fetch(file.preview, { signal: controllerRef.current.signal });
      const fileBlob = await previewResponse.blob();
      await uploadFileToS3({ 
        fileBlob, 
        upload_url: uploadUrl?.data?.upload_url,
      });

      const response = await completeFileUploadByQrCode({
        documentId: toString(documentId),
        employeeId,
        fileName: file.name,
        objectKey: uploadUrl?.data?.object_key,
        qrId,
      });

      return {
        ...file,
        id: toString(response?.data?.id),
        url: response?.data?.s3_url || '',
        canceled: false,
        // blob: responseBlob,
        // preview: URL.createObjectURL(responseBlob),
      } as FileImage & { canceled: boolean };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return { canceled: true } as FileImage & { canceled: boolean };
      }
      throw error;
    }
  }, [createDocumentByQr, createFileUploadUrlByQrCode, uploadFileToS3, completeFileUploadByQrCode, employeeId, qrId, validTo]);

  const cancelUpload = useCallback(() => {
    controllerRef.current.abort();
    cancelCreateDocument();
    cancelCreateFileUploadUrl();
    cancelUploadToS3();
    cancelCompleteFileUpload();
  }, [cancelCreateDocument, cancelCreateFileUploadUrl, cancelUploadToS3, cancelCompleteFileUpload]);

  return {
    uploadFile,
    cancelUpload,
  };
};

export default useFileUploadByQrQuery;
